import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Search, SlidersHorizontal, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Avatar } from '@/components/atoms/Avatar';
import { SEOHead } from '@/components/atoms/SEOHead';
import { BoardTabs } from '@/components/organisms/BoardTabs';
import { NEWS_CATEGORIES, type NewsCategoryKey } from '@/constants/boards';
import { timeAgo } from '@/lib/format';

interface NewsItem {
  id: number;
  category: NewsCategoryKey;
  title: string;
  summary: string | null;
  source_url: string | null;
  source_urls: string[] | null;
  source_names: string[] | null;
  is_ai_generated: boolean;
  entry_count: number | null;
  published_at: string;
  post_id: number | null;
  view_count: number;
  like_count: number;
  dislike_count: number;
  comment_count: number;
  author_user_id: string | null;
  author_nickname: string | null;
  author_image: string | null;
}

interface NewsListResponse {
  news: NewsItem[];
  total: number;
  page: number;
  limit: number;
}

const PAGE_SIZE = 20;
type SortKey = 'latest' | 'views' | 'liked';

/* 뉴스 — BoardPage 와 동일 패턴 (검색 + 필터 + 한 줄 행 리스트).
   카테고리(분류) 는 필터 드롭다운 내부 "분류" 섹션으로 이동. */
export function NewsListPage() {
  const [params, setParams] = useSearchParams();
  const category = (params.get('category') as NewsCategoryKey | null) ?? null;
  const sort = ((params.get('sort') as SortKey | null) ?? 'latest') as SortKey;
  const page = Math.max(1, Number(params.get('page') ?? 1));

  /* 검색은 즉시 쿼리를 흔들지 않고 클라이언트 측 keyword state 로 분리.
     BoardPage 와 동일하게 2글자 이상부터 서버 검색에 반영. */
  const [keyword, setKeyword] = useState<string>(params.get('q') ?? '');
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showFilters) return;
    const onDocClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowFilters(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [showFilters]);

  /* keyword 입력 → URL q 파라미터 동기화 (debounce 300ms).
     2글자 미만이면 q 제거. 페이지 1로 리셋. */
  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams(params);
      const trimmed = keyword.trim();
      if (trimmed.length >= 2) p.set('q', trimmed);
      else p.delete('q');
      p.set('page', '1');
      const next = p.toString();
      const cur = params.toString();
      if (next !== cur) setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const qParam = params.get('q') ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['news', { category, sort, page, q: qParam }],
    queryFn: async () => {
      const sp = new URLSearchParams();
      if (category) sp.set('category', category);
      sp.set('sort', sort);
      sp.set('page', String(page));
      sp.set('limit', String(PAGE_SIZE));
      if (qParam.length >= 2) sp.set('keyword', qParam);
      return get<NewsListResponse>(`/news?${sp.toString()}`);
    },
  });

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  function setCat(next: NewsCategoryKey | null) {
    const p = new URLSearchParams(params);
    if (next) p.set('category', next);
    else p.delete('category');
    p.set('page', '1');
    setParams(p);
  }
  function setSort(next: SortKey) {
    const p = new URLSearchParams(params);
    if (next === 'latest') p.delete('sort');
    else p.set('sort', next);
    p.set('page', '1');
    setParams(p);
  }
  function setPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p);
  }
  function resetFilters() {
    const p = new URLSearchParams(params);
    p.delete('category');
    p.delete('sort');
    p.set('page', '1');
    setParams(p);
  }

  const filterActive = category !== null || sort !== 'latest';

  return (
    <>
      <SEOHead
        title="증시뉴스 — 시세공간"
        description="국내증시·해외증시·코인·경제정책 증시뉴스를 한 곳에서"
      />
      <BoardTabs activeSlug="news" />
      <div className="container-base pb-6">
        <div className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
          <header className="mb-5">
            <h1 className="text-2xl font-extrabold">증시뉴스</h1>
            <p className="mt-1 text-sm text-[var(--color-text-sub)]">
              RSS 자동 수집 · 매시 정각 갱신 · 4개 카테고리 분류
            </p>
          </header>

          {/* 검색 + 필터 — BoardPage 와 동일 구조 (글쓰기 버튼은 자동 수집 게시판이므로 비노출) */}
          <div className="mb-4 flex w-full items-center gap-2">
            <div className="relative min-w-0 flex-1 sm:w-72 sm:flex-none">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-sub)]" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="증시뉴스 검색 (2글자 이상)"
                className="h-10 w-full rounded-full border border-[var(--color-border)] bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
              />
              {keyword.trim().length === 1 && (
                <p className="absolute left-3 top-full mt-1 text-[11px] text-[var(--color-text-sub)]">
                  2글자 이상 입력해 주세요.
                </p>
              )}
            </div>

            <div ref={filterRef} className="relative shrink-0">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                aria-expanded={showFilters}
                aria-haspopup="true"
                className={`inline-flex h-10 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium transition-colors sm:px-4 ${
                  showFilters
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)]'
                    : 'border-[var(--color-border)] bg-white text-[var(--color-text)] hover:border-[var(--color-primary)]/40'
                }`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span className="hidden sm:inline">필터</span>
                {filterActive && (
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary)]"
                    aria-label="필터 적용중"
                  />
                )}
              </button>

              {showFilters && (
                <div className="absolute right-0 top-full z-30 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-[var(--color-border)] bg-white shadow-lg">
                  <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2.5">
                    <span className="text-sm font-bold">필터</span>
                    <button
                      type="button"
                      onClick={() => setShowFilters(false)}
                      className="rounded-full p-1 text-[var(--color-text-sub)] hover:bg-[var(--color-bg)]"
                      aria-label="필터 닫기"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* 분류 — 4개 카테고리 + 전체 */}
                  <div className="border-b border-[var(--color-border)] px-4 py-3">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-sub)]">
                      분류
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        onClick={() => setCat(null)}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          !category
                            ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                            : 'border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-sub)] hover:text-[var(--color-text)]'
                        }`}
                      >
                        전체
                      </button>
                      {NEWS_CATEGORIES.map((c) => (
                        <button
                          key={c.key}
                          onClick={() => setCat(c.key)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            category === c.key
                              ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                              : 'border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-sub)] hover:text-[var(--color-text)]'
                          }`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 정렬 */}
                  <div className="px-4 py-3">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-sub)]">
                      정렬
                    </div>
                    <div className="flex overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)]">
                      {([
                        { key: 'latest' as const, label: '최신순', hint: '게시일 기준' },
                        { key: 'views' as const, label: '조회순', hint: '조회수 기준' },
                        { key: 'liked' as const, label: '추천순', hint: '추천수 기준' },
                      ]).map((s, i) => (
                        <button
                          key={s.key}
                          onClick={() => setSort(s.key)}
                          title={s.hint}
                          className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                            i > 0 ? 'border-l border-[var(--color-border)]' : ''
                          } ${
                            sort === s.key
                              ? 'bg-[var(--color-primary)] text-white'
                              : 'text-[var(--color-text-sub)] hover:bg-white hover:text-[var(--color-text)]'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {filterActive && (
                    <div className="border-t border-[var(--color-border)] px-4 py-2.5 text-right">
                      <button
                        onClick={resetFilters}
                        className="text-xs font-medium text-[var(--color-text-sub)] hover:text-[var(--color-primary)]"
                      >
                        초기화
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 한 줄 리스트 */}
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !data || data.news.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-sm text-[var(--color-text-sub)]">
              {qParam.length >= 2
                ? `"${qParam}" 검색 결과가 없어요.`
                : category
                ? '해당 카테고리 증시뉴스가 아직 없어요.'
                : '증시뉴스가 곧 채워질 예정이에요.'}
            </div>
          ) : (
            <>
              {/* 컬럼 헤더 — PC 전용, BoardPage 와 동일 너비 */}
              <div className="hidden items-center gap-3 border-b border-[var(--color-border)] py-2 text-sm text-gray-400 sm:flex">
                <span className="min-w-0 flex-1">제목</span>
                <span className="block min-w-[168px] max-w-[168px] text-left">작성자</span>
                <span className="inline-flex min-w-[56px] max-w-[56px] justify-end">조회수</span>
                <span className="inline-flex min-w-[44px] max-w-[44px] justify-end">추천</span>
                <span className="inline-flex min-w-[44px] max-w-[44px] justify-end">비추천</span>
                <span className="inline-flex min-w-[64px] justify-end">작성일</span>
              </div>
              <ul>
                {data.news.map((n) => {
                  const catLabel = NEWS_CATEGORIES.find((c) => c.key === n.category)?.label ?? n.category;
                  const target = n.post_id ? `/post/${n.post_id}` : (n.source_url ?? '#');
                  const external = !n.post_id && !!n.source_url;
                  const views = Math.min(99999, n.view_count ?? 0);
                  const likes = Math.min(9999, n.like_count ?? 0);
                  const dislikes = Math.min(9999, n.dislike_count ?? 0);
                  const nick = n.author_nickname ?? '시세봇';

                  const Row = (
                    <>
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="badge badge-primary">{catLabel}</span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          {n.title}
                          {n.comment_count > 0 && (
                            <span className="ml-1 font-semibold text-[var(--color-primary)]">
                              [{n.comment_count}]
                            </span>
                          )}
                        </span>
                        {external && (
                          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-sub)]" />
                        )}
                      </span>
                      <div className="hidden shrink-0 items-center gap-3 text-xs text-[var(--color-text-sub)] sm:flex">
                        <div className="flex min-w-[168px] max-w-[168px] items-center gap-1.5">
                          <Avatar
                            src={n.author_image ?? undefined}
                            alt={nick}
                            size="xs"
                            userId={n.author_user_id ?? undefined}
                          />
                          <span className="block min-w-0 flex-1 truncate font-medium text-[var(--color-text)]">
                            {nick}
                          </span>
                        </div>
                        <span className="inline-flex min-w-[56px] max-w-[56px] justify-end tabular-nums">
                          {views.toLocaleString('ko-KR')}
                        </span>
                        <span className="inline-flex min-w-[44px] max-w-[44px] justify-end tabular-nums">
                          {likes.toLocaleString('ko-KR')}
                        </span>
                        <span className="inline-flex min-w-[44px] max-w-[44px] justify-end tabular-nums">
                          {dislikes.toLocaleString('ko-KR')}
                        </span>
                        <span className="inline-flex min-w-[64px] justify-end tabular-nums">
                          {timeAgo(n.published_at)}
                        </span>
                      </div>
                    </>
                  );

                  return (
                    <li
                      key={n.id}
                      className="border-b border-[var(--color-border)] last:border-b-0"
                    >
                      {external ? (
                        <a
                          href={target}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 py-3 hover:bg-[var(--color-bg)]/60"
                        >
                          {Row}
                        </a>
                      ) : (
                        <Link
                          to={target}
                          className="flex items-center gap-3 py-3 hover:bg-[var(--color-bg)]/60"
                        >
                          {Row}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {/* 페이지네이션 — BoardPage 와 동일 숫자형 */}
          {data && totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          )}
        </div>
      </div>
    </>
  );
}

/** 숫자형 페이지네이션 — 현재 페이지 중심으로 최대 5개 버튼 (BoardPage 와 동일) */
function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (n: number) => void;
}) {
  const windowSize = 5;
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + windowSize - 1);
  start = Math.max(1, Math.min(start, end - windowSize + 1));
  const pages: number[] = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="mt-6 flex items-center justify-center gap-1">
      <button
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-sub)] hover:bg-[var(--color-bg)] disabled:opacity-40"
        aria-label="이전 페이지"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      {pages.map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`h-10 min-w-[40px] rounded-lg px-2 text-sm font-semibold transition-colors ${
            n === page
              ? 'bg-[var(--color-primary)] text-white'
              : 'border border-[var(--color-border)] text-[var(--color-text-sub)] hover:bg-[var(--color-bg)]'
          }`}
        >
          {n}
        </button>
      ))}
      <button
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-sub)] hover:bg-[var(--color-bg)] disabled:opacity-40"
        aria-label="다음 페이지"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
